import { bindColumnProps } from '@/cascade/bindColumnProps';
import { draftColumnId, isDraftColumnId, type CascadeStack } from '@/cascade/types';
import { isNarrowViewport } from '@/composables/useIsNarrow';
import { sidebars, type SidebarType } from '@/registries/sidebars';
import type { DraftDefinition } from '@/stores/draftDefinitions';
import type { DraftKey } from '@/stores/draftKey';

/**
 * The graduation rule, on its own (ADR-0029 §H-6).
 *
 * "A column that left the cascade holding work becomes a parked Entwurf" (§G) is the
 * hardest rule in the Entwurf feature, and it used to live inside a Pinia watcher that
 * reached into the router and the cascade store — so exercising one rule needed all
 * three. Here it is a function over a cascade stack, a page name, the breakpoint
 * answer, the definitions and its own memory, returning what to do. The model is
 * `cascadeReducer`: state in, decision out, tested by constructing inputs.
 *
 * WHAT STAYS IN THE STORE, and why. This module decides which columns LEFT; whether
 * what they left behind is worth keeping is a question about buffers, and answering it
 * here would drag the buffer store back in. So the plan says "this identity is gone,
 * this type has no column any more" and `draftStore` decides park-or-discard, mints the
 * instance and moves the buffer.
 */

// ---------------------------------------------------------------------------
// §E — the one home for the below-breakpoint rule
// ---------------------------------------------------------------------------

/**
 * Is there a draft zone to put an Entwurf in?
 *
 * Below `1024px` the cascade is a full-screen push-nav overlay: one column at a time,
 * no neighbour for a rail to yield width to, so the whole draft UI is off (ADR-0029
 * §E). A draft that would graduate there has nowhere to go, so it simply stays under
 * its own key — invisible, and picked up again by the next column that asks for it,
 * which is how this behaved before the zone existed.
 *
 * Exactly that product rule and nothing else. The open-time decision in `sidebarStore`
 * shares this call but keeps its own four preconditions (a zone type, a create
 * sentinel, the sidebar preference, a workspace route) locally: they are unrelated
 * tests that happen to guard the same branch, and folding them in here would make a
 * grab-bag out of one rule.
 *
 * Not to be confused with the `VITE_MODE === 'TESTING-FRONTEND'` check in
 * `DraftZoneColumn`, which is an animation switch like the ones in `CascadeColumn` and
 * `CascadeColumnStrip`.
 */
export const draftZoneAvailable = (): boolean => !isNarrowViewport();

// ---------------------------------------------------------------------------
// The policy's memory
// ---------------------------------------------------------------------------

/** An identified column as it was while it was still in the cascade. */
export interface IdentifiedSighting {
  key: DraftKey;
  type: SidebarType;
  /** The props the column really had — see {@link GraduationMemory.activeIdentified}. */
  props: Record<string, unknown>;
}

/** A sighting plus the page it was on and what it called itself. */
export interface RecordedSighting extends IdentifiedSighting {
  routeName: string;
  label?: string;
}

/**
 * What the policy remembers between passes.
 *
 * MEMORY, not data: nothing renders from any of it, and one of the four exists purely
 * because a column's props are readable while it is in the stack and not after. Plain
 * `Map`s rather than reactive state on purpose — making them reactive would re-trigger
 * the very watcher that writes them.
 *
 * Handed in rather than reached for, so a test can construct one and assert against it
 * without a Pinia instance. The app's single memory is {@link graduationMemory}.
 */
export interface GraduationMemory {
  /**
   * Page each type's active `:new` column was last seen on, so a graduation that
   * happens BECAUSE of a page change files the draft under the page it belongs to and
   * not the one the user just landed on.
   */
  activeColumnRoutes: Map<SidebarType, string>;
  /**
   * Every IDENTIFIED column seen in the cascade, by its draft key, with what a zone
   * instance would need to take it over.
   *
   * Recorded while the column is still there because that is the only time it can be:
   * graduation happens BECAUSE the column is gone — the page changed, an ancestor
   * closed — and by then there is nothing left to read the patient id or the page off.
   */
  activeIdentified: Map<DraftKey, RecordedSighting>;
  /**
   * What each draft panel calls itself, last reported while it was mounted — keyed the
   * same way its state is (the bare type for the active create column, an identity for
   * an identified one, an instance id once parked).
   *
   * Reported BY the panel rather than read from it: the panel is the only thing that
   * knows what its draft is OF — the patient a Termin is for, the Standort's name, the
   * email an invitation is going to — and none of that is derivable from the ids the
   * store holds.
   *
   * Kept apart from the sightings because it outlives every one of them: a name
   * resolves from queries and can land after a column was first seen, and it has to
   * survive the remount that restoring a draft performs.
   */
  draftLabels: Map<DraftKey, string>;
  /**
   * How many create columns of a type this session has opened. The id of the next one
   * (`new`, then `new~2`, …) is what makes replacing one a real remount rather than a
   * component reuse — see `isDraftColumnId`.
   */
  activeColumnGenerations: Map<SidebarType, number>;
}

export function createGraduationMemory(): GraduationMemory {
  return {
    activeColumnRoutes: new Map(),
    activeIdentified: new Map(),
    draftLabels: new Map(),
    activeColumnGenerations: new Map(),
  };
}

/**
 * Forget everything. Explicit rather than a fresh object, because `graduationMemory`
 * below is a module singleton the store closes over — replacing it would leave the
 * store holding the old one.
 */
export function resetGraduationMemory(memory: GraduationMemory): void {
  memory.activeColumnRoutes.clear();
  memory.activeIdentified.clear();
  memory.draftLabels.clear();
  memory.activeColumnGenerations.clear();
}

/** The app's one memory, written by `draftStore`'s watchers and services. */
export const graduationMemory: GraduationMemory = createGraduationMemory();

/** The id the next create column of this type must carry. */
export function mintNextColumnId(memory: GraduationMemory, type: SidebarType): string {
  const generation = (memory.activeColumnGenerations.get(type) ?? 1) + 1;
  memory.activeColumnGenerations.set(type, generation);
  return draftColumnId(generation);
}

// ---------------------------------------------------------------------------
// Surveying the cascade
// ---------------------------------------------------------------------------

/** The draft columns standing in the cascade right now, and nothing else about it. */
export interface CascadeSurvey {
  /**
   * Types with a generational `:new` create column open, with the column's id.
   *
   * The id is load-bearing as well as the type: when a graduation hands the slot to the
   * next generation, `new` becomes `new~2` — the same type, a different column. A
   * types-only answer did not change there, so nothing re-recorded the page, and the
   * NEXT graduation filed its draft under whatever page the user had since navigated to.
   */
  createColumns: readonly { type: SidebarType; id: string }[];
  /** Identified draft columns open right now, with their bound props. */
  identified: readonly IdentifiedSighting[];
}

/**
 * Reduce a cascade stack to its draft columns. Pure over the stack and the
 * definitions — the sidebar registry it reads for ancestor bindings is a static
 * declaration, not state.
 */
export function surveyDraftColumns(
  stack: CascadeStack,
  definitions: readonly DraftDefinition[],
): CascadeSurvey {
  const definitionFor = (type: string): DraftDefinition | undefined =>
    definitions.find((entry) => entry.type === type);
  const createColumns: { type: SidebarType; id: string }[] = [];
  const identified: IdentifiedSighting[] = [];
  for (const column of stack) {
    const definition = definitionFor(column.type);
    if (!definition?.zone) continue;
    if (isDraftColumnId(column.id)) {
      createColumns.push({ type: definition.type, id: column.id });
    }
    if (!definition.keyFor) continue;
    const props = bindColumnProps(
      stack,
      column,
      sidebars.find((sidebar) => sidebar.type === column.type)?.inline,
    );
    const key = definition.keyFor(props);
    if (key) identified.push({ key, type: definition.type, props });
  }
  return { createColumns, identified };
}

/**
 * A string that changes exactly when the survey does, for the watcher to compare.
 *
 * A string rather than the survey itself, so an unrelated cascade change — a row pick,
 * a drill-in, an ancestor closing — does not re-run the rule. The two kinds are kept in
 * SEPARATE lists rather than concatenated: flattened, the reader had to recover a type
 * by cutting each entry at its first `:`, which is also how an identity key is
 * punctuated, so `customFormDetail` read as having an active create column whenever an
 * EDIT column of it was open, and that type's create draft then never graduated.
 *
 * This is a watcher signal and nothing more. Nothing is parsed back out of it: the
 * handler re-surveys and works on real keys, so a key can no longer fail to match
 * itself after a round trip.
 */
export function graduationSignal(survey: CascadeSurvey): string {
  return JSON.stringify({
    generational: survey.createColumns,
    identified: survey.identified.map((entry) => entry.key),
  });
}

// ---------------------------------------------------------------------------
// The rule
// ---------------------------------------------------------------------------

export interface GraduationInput {
  survey: CascadeSurvey;
  /** `route.name` of the page the user is on now. */
  routeName: string;
  /** {@link draftZoneAvailable}, resolved by the caller so this stays testable. */
  zoneAvailable: boolean;
  definitions: readonly DraftDefinition[];
  memory: GraduationMemory;
}

export interface GraduationPlan {
  /**
   * Identified drafts whose column has left the cascade, with everything a zone
   * instance needs to take one over. Forgotten by the policy as they are reported, so
   * the same identity cannot be handed over twice.
   */
  staleIdentities: RecordedSighting[];
  /**
   * Types with no active create column standing in the cascade any more.
   *
   * Includes types that never had one on this pass — the store asks whether each holds
   * work before parking anything, and a type with nothing in its buffer is a no-op
   * there. Deciding it here would mean asking the buffer store, which is what §H-6
   * takes out.
   */
  graduatingTypes: SidebarType[];
  /**
   * `[type, page]` filed away because that type's create column is still standing — the
   * third of the rule's three outputs, beside the two above.
   *
   * The recording itself happens in the memory, because that is where a graduation two
   * navigations later will look for it. This is the same fact stated as a RETURN value,
   * so that a caller — and a test — can see what the rule decided without reading a map
   * it has no business knowing the shape of.
   */
  routesRecorded: [SidebarType, string][];
}

/**
 * Decide what a cascade change means for the drafts.
 *
 * Free of Pinia, the router and the cascade store; the {@link GraduationMemory} it is
 * handed is the only thing it writes, and it writes it for two reasons — to record what
 * is readable only now, and to forget what it has just reported as gone.
 *
 * Idempotent by construction: a stale identity is forgotten as it is reported, and a
 * graduating type's key is renamed away by the store, so neither condition can hold
 * twice for the same draft.
 */
export function planGraduation(input: GraduationInput): GraduationPlan {
  const { survey, routeName, zoneAvailable, definitions, memory } = input;

  // Record first, decide second. An identified column is readable only while it is
  // standing, and a sighting taken on this pass is by definition not stale on it.
  for (const sighting of survey.identified) {
    memory.activeIdentified.set(sighting.key, {
      ...sighting,
      routeName,
      // Whatever the panel had called itself by the time this rule last ran. Re-read on
      // every pass rather than captured once, because both halves of that name arrive
      // from queries and can land after the column was first seen.
      //
      // A pass is a CHANGE to the draft columns, though — not every reactive tick — so a
      // name landing while nothing moves does not reach this copy. That is why
      // `draftStore.graduateIdentifiedColumn` prefers a LIVE read of `draftLabels` over
      // the value here: this is the last-seen fallback, and the live map is the answer.
      label: memory.draftLabels.get(sighting.key),
    });
  }

  // An identity that was in the cascade and is not any more: the column left without
  // anyone closing it, so its answers graduate. Below the breakpoint there is no zone
  // to graduate into, so the record simply stays and the column comes back to it.
  const staleIdentities: RecordedSighting[] = [];
  if (zoneAvailable) {
    const openIdentities = new Set(survey.identified.map((sighting) => sighting.key));
    for (const [key, record] of memory.activeIdentified) {
      if (!openIdentities.has(key)) staleIdentities.push(record);
    }
    for (const record of staleIdentities) memory.activeIdentified.delete(record.key);
  }

  const openTypes = new Set(survey.createColumns.map((column) => column.type));
  const graduatingTypes: SidebarType[] = [];
  const routesRecorded: [SidebarType, string][] = [];
  for (const definition of definitions) {
    if (!definition.zone) continue;
    // A purely identified type has no per-type active column to graduate — its columns
    // are handled above, one identity at a time. A type that is BOTH (`customFormDetail`)
    // still has one, and skipping it here left the create draft to vanish on a page
    // change.
    if (definition.identifiedOnly) continue;
    if (openTypes.has(definition.type)) {
      memory.activeColumnRoutes.set(definition.type, routeName);
      routesRecorded.push([definition.type, routeName]);
      continue;
    }
    if (!zoneAvailable) continue;
    graduatingTypes.push(definition.type);
  }

  return { staleIdentities, graduatingTypes, routesRecorded };
}

// ---------------------------------------------------------------------------
// Settling the zone on a page change
// ---------------------------------------------------------------------------

/** The little of an instance that settling looks at. */
export interface SettleableInstance {
  id: DraftKey;
  minimized: boolean;
  /**
   * The panel shown beside this draft, if any. `unknown` because only its PRESENCE is
   * read here — its shape (`DraftZoneChildSpec`) is declared by the store, which imports
   * this module, so naming it would close a cycle to gain nothing.
   */
  child?: unknown;
}

export interface SettleInput {
  instances: readonly SettleableInstance[];
  dirtyIds: ReadonlySet<DraftKey>;
}

export interface SettlePlan {
  /** Pristine instances, to be destroyed outright along with their buffers. */
  destroy: DraftKey[];
  /** Instances holding work, to be collapsed and stripped of any panel beside them. */
  park: DraftKey[];
}

/**
 * Leaving the page settles every instance: the pristine ones are destroyed and the ones
 * holding work are collapsed, so returning shows rails over the list rather than three
 * forms covering it.
 *
 * An EMPTY plan is meaningful and load-bearing: it is what tells the store to leave the
 * instances array — and the identity of every object in it — untouched. Drilling around
 * inside one page changes the cascade path but not the page name, and the zone moves
 * only when the user moves it.
 *
 * Parking drops the child panel, exactly as collapsing a draft by hand does: the form
 * preview is a detail OF the draft, so coming back to the page to find it reopened
 * beside a rail would restore a view the user never asked to keep.
 */
export function planSettle(input: SettleInput): SettlePlan {
  const destroy: DraftKey[] = [];
  const park: DraftKey[] = [];
  for (const instance of input.instances) {
    if (!input.dirtyIds.has(instance.id)) {
      destroy.push(instance.id);
      continue;
    }
    if (!instance.minimized || instance.child) park.push(instance.id);
  }
  return { destroy, park };
}
